import {
    ButtonHTMLAttributes,
    forwardRef,
    InputHTMLAttributes,
    ReactNode,
    useId,
    useState,
} from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

/** Small inline spinner tinted to the current text color. */
function Spinner({ className }: { className?: string }) {
    return (
        <svg
            className={clsx("animate-spin", className)}
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
        >
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            />
            <path
                className="opacity-90"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z"
            />
        </svg>
    );
}

type BrandButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: "primary" | "outline";
    loading?: boolean;
    children: ReactNode;
};

/** Pill button — filled pink (primary) or outlined purple (secondary). */
export const BrandButton = forwardRef<HTMLButtonElement, BrandButtonProps>(
    function BrandButton(
        {
            variant = "primary",
            loading,
            disabled,
            className,
            children,
            ...rest
        },
        ref,
    ) {
        return (
            <button
                ref={ref}
                disabled={disabled || loading}
                // Merged, so a caller's width or height replaces the default
                // instead of losing to it on stylesheet order.
                className={twMerge(
                    clsx(
                        "flex h-10 w-full items-center justify-center gap-2.5 rounded-full px-4 text-base font-medium transition-colors",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink focus-visible:ring-offset-2",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        variant === "primary" &&
                            "bg-brand-pink text-white hover:bg-brand-pink-hover",
                        variant === "outline" &&
                            "border border-brand-purple/20 bg-white text-brand-purple hover:bg-brand-beige-light",
                    ),
                    className,
                )}
                {...rest}
            >
                {loading && <Spinner className="h-5 w-5" />}
                {children}
            </button>
        );
    },
);

type BrandInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
    label: string;
    type?: "text" | "email" | "password";
    error?: string;
    /** Renders a show/hide toggle; forces the input to behave as a password. */
    revealable?: boolean;
};

/** Labelled input — beige border, radius 8, pink focus ring, optional reveal. */
export const BrandInput = forwardRef<HTMLInputElement, BrandInputProps>(
    function BrandInput(
        { label, type = "text", error, revealable, id, className, ...rest },
        ref,
    ) {
        const generatedId = useId();
        const inputId = id ?? generatedId;
        const [revealed, setRevealed] = useState(false);
        const resolvedType = revealable
            ? revealed
                ? "text"
                : "password"
            : type;

        return (
            <div className="flex flex-col gap-2">
                <label
                    htmlFor={inputId}
                    className="text-xs text-brand-grey-warm"
                >
                    {label}
                </label>
                <div className="relative">
                    <input
                        ref={ref}
                        id={inputId}
                        type={resolvedType}
                        className={clsx(
                            "h-10 w-full rounded-lg border bg-white px-3 text-base text-brand-purple placeholder:text-brand-grey",
                            "focus:outline-none focus:ring-2 focus:ring-brand-pink/40",
                            revealable && "pr-10",
                            error
                                ? "border-brand-pink focus:ring-brand-pink/40"
                                : "border-brand-beige focus:border-brand-pink",
                            className,
                        )}
                        aria-invalid={error ? true : undefined}
                        aria-describedby={
                            error ? `${inputId}-error` : undefined
                        }
                        {...rest}
                    />
                    {revealable && (
                        <button
                            type="button"
                            onClick={() => setRevealed((v) => !v)}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-brand-grey hover:text-brand-purple"
                            aria-label={
                                revealed ? "Hide password" : "Show password"
                            }
                            tabIndex={-1}
                        >
                            {revealed ? (
                                <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                                <EyeIcon className="h-5 w-5" />
                            )}
                        </button>
                    )}
                </div>
                {error && (
                    <p
                        id={`${inputId}-error`}
                        className="text-xs text-brand-pink"
                    >
                        {error}
                    </p>
                )}
            </div>
        );
    },
);

/** "Continue with Google" — outlined pill with the multi-color Google mark. */
export function GoogleButton({
    onClick,
    disabled,
}: {
    onClick: () => void;
    disabled?: boolean;
}) {
    return (
        <BrandButton variant="outline" onClick={onClick} disabled={disabled}>
            <svg viewBox="0 0 48 48" className="h-6 w-6" aria-hidden="true">
                <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                />
                <path
                    fill="#4285F4"
                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                />
                <path
                    fill="#FBBC05"
                    d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                />
                <path
                    fill="#34A853"
                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                />
            </svg>
            Continue with Google
        </BrandButton>
    );
}

/** Horizontal "or" divider. */
export function OrDivider() {
    return (
        <div className="flex items-center gap-5">
            <span className="h-px flex-1 bg-brand-beige" />
            <span className="font-brand-mono text-xs text-brand-grey-light">
                or
            </span>
            <span className="h-px flex-1 bg-brand-beige" />
        </div>
    );
}

/** Heading + subtitle block used at the top of every auth card. */
export function AuthHeading({
    title,
    subtitle,
}: {
    title: string;
    subtitle?: ReactNode;
}) {
    return (
        <div className="flex flex-col gap-2">
            <h1 className="font-display text-3xl leading-tight text-brand-purple">
                {title}
            </h1>
            {subtitle && (
                <p className="text-base text-brand-grey-warm">{subtitle}</p>
            )}
        </div>
    );
}

/** A top-level form error banner (e.g. wrong password). */
export function AuthError({ message }: { message?: string }) {
    if (!message) {
        return null;
    }
    return (
        <p className="rounded-lg bg-brand-pink/10 px-3 py-2 text-sm text-brand-pink">
            {message}
        </p>
    );
}

/** Muted footer line with a single pink call-to-action link. */
export function AuthFooterLink({
    prompt,
    linkLabel,
    onClick,
    href,
}: {
    prompt: string;
    linkLabel: string;
    onClick?: () => void;
    href?: string;
}) {
    return (
        <p className="text-center text-xs text-brand-grey">
            {prompt}{" "}
            {href ? (
                <a
                    href={href}
                    className="font-medium text-brand-pink hover:underline"
                >
                    {linkLabel}
                </a>
            ) : (
                <button
                    type="button"
                    onClick={onClick}
                    className="font-medium text-brand-pink hover:underline"
                >
                    {linkLabel}
                </button>
            )}
        </p>
    );
}
